import React, { useRef, useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import { useStyles } from './styles';
import ControlledError from '../ControlledError';

interface OtpInputProps {
  length?: number;
  disabled?: boolean;
  error?: string;
  allowCharacters?: boolean;
  styleClass?: string;
  inputsParentClass?: string;
  /** Base id for automation. Each digit box gets `${testId}-${index}` (0-based). */
  testId?: string;
  onComplete: (otp: string) => void;
  onResetOtp?: () => void;
  onChange?: (otp: string) => void;
  autoFocus?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  disabled,
  error,
  allowCharacters = true,
  styleClass,
  inputsParentClass,
  testId,
  onComplete,
  onResetOtp,
  onChange,
  autoFocus = false,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const [isOtpComplete, setIsOtpComplete] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const classes = useStyles();

  // Auto-focus first input on mount if autoFocus is enabled
  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  // Trigger callback when all fields are filled
  useEffect(() => {
    if (otp.every(val => val.length === 1)) {
      setIsOtpComplete(true);
      onComplete(otp.join(''));
    } else {
      if (isOtpComplete) {
        setIsOtpComplete(false);
        onResetOtp?.();
      }
    }
  }, [otp, isOtpComplete, onComplete, onResetOtp]);

  useEffect(() => {
    onChange?.(otp?.join?.(''));
  }, [otp]);

  const handleInput = (e: any, idx: number) => {
    const val = allowCharacters
      ? e.target.value?.replace(/[^a-zA-Z0-9]/g, '')?.toUpperCase()
      : e.target.value?.replace(/\D/g, '');
    if (!val) return;

    const newOtp = [...otp];
    newOtp[idx] = val[val.length - 1]; // always take the most recent digit
    setOtp(newOtp);

    // Move to next field regardless of same number
    if (idx < length - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<any>, idx: number) => {
    const curVal = otp[idx];

    switch (e.key) {
      case 'Backspace':
        if (curVal === '') {
          if (idx > 0) {
            const newOtp = [...otp];
            newOtp[idx - 1] = '';
            setOtp(newOtp);
            inputsRef.current[idx - 1]?.focus();
          }
        } else {
          const newOtp = [...otp];
          newOtp[idx] = '';
          setOtp(newOtp);
        }
        e.preventDefault();
        break;

      case 'Delete': {
        const newOtp = [...otp];
        newOtp[idx] = '';
        setOtp(newOtp);
        e.preventDefault();
        break;
      }

      case 'ArrowLeft':
        if (idx > 0) {
          inputsRef.current[idx - 1]?.focus();
        }
        e.preventDefault();
        break;

      case 'ArrowRight':
        if (idx < length - 1) {
          inputsRef.current[idx + 1]?.focus();
        }
        e.preventDefault();
        break;

      default:
        break;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      ?.replace(allowCharacters ? /[^a-zA-Z0-9]/g : /\D/g, '')
      ?.toUpperCase()
      ?.split('');
    if (!pasted.length) return;

    const newOtp = [...otp];
    pasted.slice(0, length).forEach((char, i) => {
      newOtp[i] = char;
    });

    setOtp(newOtp);
    const lastFilled = Math.min(pasted.length, length - 1);
    inputsRef.current[lastFilled]?.focus();
  };

  // Select the text inside the box whenever it gets focused
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      e.target.select();
    }
  };

  return (
    <div className={styleClass} data-testid={testId}>
      <ControlledError show={!!error} error={!!error && error}>
        <Box display="inline-flex" gap={2} className={inputsParentClass}>
          {otp.map((digit, idx) => (
            <TextField
              key={idx}
              value={digit}
              inputRef={el => (inputsRef.current[idx] = el)}
              variant="outlined"
              data-testid={testId ? `${testId}-${idx}` : undefined}
              inputProps={{
                maxLength: 1,
                inputMode: allowCharacters ? 'text' : 'numeric',
                'aria-label': `OTP Digit ${idx + 1}`,
                autoComplete: 'off',
              }}
              disabled={disabled}
              error={!!error}
              className={classes.otpInput}
              onInput={e => handleInput(e as React.FormEvent<HTMLInputElement>, idx)}
              onKeyDown={e => handleKeyDown(e, idx)}
              onPaste={handlePaste}
              onFocus={handleFocus}
            />
          ))}
        </Box>
      </ControlledError>
    </div>
  );
};

export default OtpInput;
